import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  Home,
  Building2,
  Headphones,
  FileCode2,
  BarChart3,
  UserCog,
  Radio,
  ScrollText,
  Settings as Cog,
  Activity,
  MessageSquare,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn.js";
import { useAuthStore } from "@/lib/auth.js";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  perm?: string;
}

const items: NavItem[] = [
  { to: "/", label: "Inicio", icon: Home },
  { to: "/companies", label: "Companies", icon: Building2, perm: "company.read" },
  { to: "/agents", label: "Agentes", icon: Headphones, perm: "agent.read" },
  { to: "/sessions", label: "Sesiones activas", icon: Activity, perm: "session.read" },
  { to: "/templates", label: "Plantillas HSM", icon: FileCode2, perm: "template.read" },
  { to: "/reporting", label: "Reportería", icon: BarChart3, perm: "reporting.read" },
  { to: "/users", label: "Usuarios", icon: UserCog, perm: "user.read" },
  { to: "/channel-health", label: "Salud de canales", icon: Radio, perm: "channel.read" },
  { to: "/webchats", label: "Webchats", icon: MessageSquare, perm: "webchat.read" },
  { to: "/audit", label: "Audit log", icon: ScrollText, perm: "audit.read" },
  { to: "/settings", label: "Configuración", icon: Cog },
];

/**
 * Menú lateral. Desde md queda fijo a la izquierda; en pantallas chicas es un
 * panel que se abre con el botón de menú de la barra superior y se cierra al
 * elegir una sección, con Escape o tocando afuera.
 */
export function Sidebar({ abierto = false, onCerrar }: Readonly<{ abierto?: boolean; onCerrar?: () => void }>) {
  const user = useAuthStore((s) => s.user);
  const has = (perm?: string) => !perm || (user?.permissions.includes(perm) ?? false);

  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCerrar?.();
    };
    document.addEventListener("keydown", alTeclear);
    return () => document.removeEventListener("keydown", alTeclear);
  }, [abierto, onCerrar]);

  return (
    <>
      {abierto && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-slate-950/50 md:hidden"
          aria-label="Cerrar el menú"
          onClick={onCerrar}
        />
      )}
      <aside
        id="menu-principal"
        className={cn(
          "w-[240px] shrink-0 bg-slate-950 text-slate-300 flex flex-col",
          "fixed inset-y-0 left-0 z-40 transition-transform md:static md:translate-x-0",
          abierto ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="px-5 h-14 flex items-center gap-2 border-b border-slate-800">
          <div className="w-7 h-7 rounded-md bg-brand-500 grid place-items-center text-white font-bold text-xs">iS</div>
          <span className="font-semibold text-white">ISOFT</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items
            .filter((i) => has(i.perm))
            .map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.to === "/"}
                onClick={onCerrar}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    isActive ? "bg-brand-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-slate-100",
                  )
                }
              >
                <i.icon size={16} />
                <span>{i.label}</span>
              </NavLink>
            ))}
        </nav>
        <div className="p-3 border-t border-slate-800 text-xs text-slate-500">
          <p>v0.1.0 · backoffice</p>
        </div>
      </aside>
    </>
  );
}
